import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import loader from "../assets/loader.json";
import { useRoiHistoryQuery } from "../redux/features/user/userApi";
import Navbar from "../Components/Navbar";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, parse } from "date-fns";

const parseDate = (dateString) => {
  if (typeof dateString === "string" && dateString.includes("/")) {
    // Parse as DD/MM/YYYY
    return parse(dateString, "dd/MM/yyyy", new Date());
  }
  return new Date(dateString);
};

const isValidDate = (d) => d instanceof Date && !isNaN(d);

const RoiHistory = () => {
    const navigate = useNavigate();
    const { data, isSuccess, isError, isLoading } = useRoiHistoryQuery();
    const [transactions, setTransactions] = useState([]);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;
    // Date filter states
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    // Interval state for graph
    const [interval, setInterval] = useState("day"); // day, week, month, year

    useEffect(() => {
        if (isSuccess && data?.data) {
            setTransactions(data.data);
        }
    }, [isSuccess, data]);

    // Format date function
    const formatDate = (dateString) => {
        const date = parseDate(dateString);
        return isValidDate(date)
          ? date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            //   hour: '2-digit',
            //   minute: '2-digit'
            })
          : "-";
    };

    // Handle view transaction
    const handleViewTransaction = (transaction) => {
        // Navigate to single transaction page with transaction ID
        navigate(`/roi/${transaction._id}`);
    };

    // Filter transactions based on search and date range
    const filteredTransactions = transactions
        .filter(txn => {
            const dateObj = parseDate(txn.date);
            const matchesSearch =
                txn.email?.toLowerCase().includes(search.toLowerCase()) ||
                txn.plantname?.toLowerCase().includes(search.toLowerCase()) ||
                (isValidDate(dateObj) && dateObj.toLocaleDateString('en-US').includes(search));
            let matchesDate = true;
            if (startDate && isValidDate(dateObj)) {
                matchesDate = matchesDate && dateObj >= new Date(startDate);
            }
            if (endDate && isValidDate(dateObj)) {
                const end = new Date(endDate);
                end.setDate(end.getDate() + 1);
                matchesDate = matchesDate && dateObj < end;
            }
            return matchesSearch && matchesDate;
        })
        .sort((a, b) => parseDate(b.date) - parseDate(a.date));

    // --- ROI Graph Data ---
    const groupedGraphData = React.useMemo(() => {
        const group = {};
        filteredTransactions.forEach((txn) => {
            if (!txn.date) return;
            const dateObj = parseDate(txn.date);
            if (!isValidDate(dateObj)) return;
            let key = "";
            if (interval === "day") {
                key = format(dateObj, "yyyy-MM-dd");
            } else if (interval === "week") {
                const weekNumber = Math.ceil(dateObj.getDate() / 7);
                key = `${dateObj.getFullYear()}-W${String(weekNumber).padStart(2, "0")}`;
            } else if (interval === "month") {
                key = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, "0")}`;
            } else if (interval === "year") {
                key = `${dateObj.getFullYear()}`;
            }
            if (!group[key]) group[key] = { count: 0, sumPerunit: 0 };
            group[key].count += 1;
            group[key].sumPerunit += Number(txn.perunit) || 0;
        });

        // Helper: get min/max date from filteredTransactions
        const getMinMaxDate = () => {
            if (!filteredTransactions.length) return [null, null];
            let min = null, max = null;
            filteredTransactions.forEach(txn => {
                const d = parseDate(txn.date);
                if (!isValidDate(d)) return;
                if (!min || d < min) min = d;
                if (!max || d > max) max = d;
            });
            return [min, max];
        };

        // For 'day' interval, fill all days in the selected month
        if (interval === "day" && filteredTransactions.length > 0) {
            const firstValid = filteredTransactions.find(txn => isValidDate(parseDate(txn.date)));
            if (!firstValid) return [];
            const firstDate = parseDate(firstValid.date);
            const monthStart = startOfMonth(firstDate);
            const monthEnd = endOfMonth(firstDate);
            const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
            return allDays.map(date => {
                const key = format(date, "yyyy-MM-dd");
                return {
                    label: format(date, "MMM dd"),
                    count: group[key]?.count || 0,
                    sumPerunit: group[key]?.sumPerunit || 0,
                };
            });
        }
        // For 'week' interval, fill all weeks in the range
        if (interval === "week" && filteredTransactions.length > 0) {
            const [min, max] = getMinMaxDate();
            if (!min || !max) return [];
            let weeks = [];
            let current = new Date(min);
            current.setDate(1);
            let end = new Date(max);
            end.setDate(1);
            end.setMonth(end.getMonth() + 1);
            while (current <= end) {
                const year = current.getFullYear();
                const month = current.getMonth();
                for (let w = 1; w <= 5; w++) {
                    const key = `${year}-W${String(w).padStart(2, "0")}`;
                    weeks.push(key);
                }
                current.setMonth(current.getMonth() + 1);
            }
            weeks = [...new Set(weeks)];
            return weeks.map(k => {
                const [year, week] = k.split('-W');
                return {
                    label: `W${week} ${year}`,
                    count: group[k]?.count || 0,
                    sumPerunit: group[k]?.sumPerunit || 0,
                };
            });
        }
        // For 'month' interval, fill all months in the range
        if (interval === "month" && filteredTransactions.length > 0) {
            const [min, max] = getMinMaxDate();
            if (!min || !max) return [];
            let months = [];
            let current = new Date(min.getFullYear(), min.getMonth(), 1);
            const end = new Date(max.getFullYear(), max.getMonth(), 1);
            while (current <= end) {
                const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, "0")}`;
                months.push(key);
                current.setMonth(current.getMonth() + 1);
            }
            return months.map(k => {
                const [year, month] = k.split('-');
                return {
                    label: format(new Date(year, Number(month) - 1), "MMM yyyy"),
                    count: group[k]?.count || 0,
                    sumPerunit: group[k]?.sumPerunit || 0,
                };
            });
        }
        // For 'year' interval, fill all years in the range
        if (interval === "year" && filteredTransactions.length > 0) {
            const [min, max] = getMinMaxDate();
            if (!min || !max) return [];
            let years = [];
            for (let y = min.getFullYear(); y <= max.getFullYear(); y++) {
                years.push(String(y));
            }
            return years.map(k => ({
                label: k,
                count: group[k]?.count || 0,
                sumPerunit: group[k]?.sumPerunit || 0,
            }));
        }
        // Sort keys
        const sortedKeys = Object.keys(group).sort();
        return sortedKeys.map((k) => ({
            label: k,
            count: group[k]?.count || 0,
            sumPerunit: group[k]?.sumPerunit || 0,
        }));
    }, [filteredTransactions, interval]);

    // --- Stats for previous interval in current filter ---
    const prevInterval = groupedGraphData.length > 1 ? groupedGraphData[groupedGraphData.length - 2] : { count: 0, sumPerunit: 0 };
    const totalRoiCount = prevInterval.count;
    const totalPerunitSum = prevInterval.sumPerunit.toFixed(2);

    const graphOptions = React.useMemo(() => ({
        chart: {
            type: "areaspline",
            style: {
                fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
            },
            backgroundColor: "#fff",
            borderRadius: 12,
            spacing: [20, 20, 20, 20],
            animation: { duration: 1000 },
            height: 320,
        },
        title: { text: 'ROI per Day', align: 'left' },
        xAxis: {
            categories: groupedGraphData.map((item) => item.label),
            title: { text: 'Date', style: { fontSize: '12px', fontWeight: '600', color: '#64748B' } },
            gridLineWidth: 1,
            gridLineColor: 'rgba(100, 116, 139, 0.1)',
            lineColor: 'rgba(100, 116, 139, 0.2)',
            tickColor: 'rgba(100, 116, 139, 0.2)',
            labels: { style: { fontSize: '11px', color: '#64748B', fontWeight: '500' } },
            accessibility: { description: 'Dates' },
        },
        yAxis: {
            title: { text: 'Number of ROI / Sum of Generated Unit', style: { fontSize: '12px', fontWeight: '600', color: '#64748B' } },
            gridLineColor: 'rgba(100, 116, 139, 0.1)',
            labels: { style: { fontSize: '11px', color: '#64748B', fontWeight: '500' } },
            min: 0,
            allowDecimals: false,
            accessibility: { description: 'Number of ROI and sum of perunit' },
        },
        tooltip: {
            backgroundColor: '#FFFFFF',
            borderWidth: 0,
            borderRadius: 8,
            shadow: true,
            style: { fontSize: '13px', color: '#1e293b' },
            headerFormat: `<span style=\"font-weight: 600; font-size: 13px; color: #1E293B\">{point.key}</span><br/>`,
            // Use formatter for 0 decimals on count, 2 decimals on sum series
            formatter: function() {
                let s = `<span style='font-weight: 600; font-size: 13px; color: #1E293B'>${this.x}</span><br/>`;
                this.points.forEach(point => {
                    let value;
                    if (point.series.name === 'Sum of Generated Unit') {
                        value = typeof point.y === 'number' ? point.y.toFixed(2) : point.y;
                    } else {
                        value = typeof point.y === 'number' ? point.y.toFixed(0) : point.y;
                    }
                    const color = point.series.name === 'Sum of Generated Unit' ? '#22c55e' : '#2563eb';
                    s += `<span style='color: ${color}; font-weight: 500'>${value}</span> ${point.series.name}<br/>`;
                });
                return s;
            },
            shared: true,
            useHTML: true,
        },
        plotOptions: {
            areaspline: {
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, 'rgba(37, 99, 235, 0.18)'],
                        [1, 'rgba(37, 99, 235, 0.03)'],
                    ],
                },
                lineWidth: 3,
                color: '#2563eb',
                states: { hover: { lineWidth: 4 } },
                marker: {
                    enabled: true,
                    radius: 5,
                    symbol: 'circle',
                    fillColor: '#fff',
                    lineWidth: 2,
                    lineColor: '#2563eb',
                    states: { hover: { enabled: true, radius: 7, fillColor: '#fff' } },
                },
                threshold: null,
            },
        },
        series: [
            {
                name: 'ROI Count',
                data: groupedGraphData.map((item) => item.count),
                color: '#2563eb',
                fillOpacity: 0.3,
                accessibility: { description: 'Number of ROI per interval' },
            },
            {
                name: 'Sum of Generated Unit',
                data: groupedGraphData.map((item) => item.sumPerunit),
                color: '#22c55e',
                fillOpacity: 0.15,
                type: 'areaspline',
                accessibility: { description: 'Sum of perunit per interval' },
                yAxis: 0,
            },
        ],
        credits: { enabled: false },
        legend: { enabled: true },
        responsive: {
            rules: [
                {
                    condition: { maxWidth: 600 },
                    chartOptions: {
                        chart: { spacing: [8, 8, 8, 8], height: 220 },
                        xAxis: { labels: { rotation: -45 } },
                    },
                },
            ],
        },
        accessibility: {
            description: `Chart showing ROI count and sum of perunit per interval`,
            screenReaderSection: {
                beforeChartFormat:
                    '<h5>{chartTitle}</h5>' +
                    '<div>{chartSubtitle}</div>' +
                    '<div>{chartLongdesc}</div>' +
                    '<div>{viewTableButton}</div>',
            },
        },
    }), [groupedGraphData]);

    // Calculate paginated transactions
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const paginatedTransactions = filteredTransactions.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // CSV Export function
    const exportToCSV = () => {
        if (!filteredTransactions.length) return;
        const headers = ["Username","Email", "Plantname", "Generated units", "Date"];
        const csvRows = [
            headers.join(","),
            ...filteredTransactions.map(txn =>
                [
                    txn.username,
                    txn.email,
                    txn.plantname,
                    txn.perunit,
                    isValidDate(parseDate(txn.date)) ? formatDate(txn.date) : ""
                ].join(",")
            )
        ];
        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "roi_history.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <div className="flex items-center justify-center h-96">
                    <div className="w-32 h-32">
                        <Lottie animationData={loader} loop={true} />
                    </div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading ROI History</h2>
                        <p className="text-gray-600">Please try again later.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="container mx-auto px-4 py-8">
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {/* ROI History Graph */}
                    <div className="px-6 pt-6">
                        <div className="flex items-center gap-2 mb-4">
                            {['day', 'week', 'month', 'year'].map((val) => (
                                <button
                                    key={val}
                                    onClick={() => setInterval(val)}
                                    className={`px-4 py-1 rounded-lg font-semibold transition-colors duration-150 ${interval === val ? 'bg-blue-600 text-white shadow' : 'bg-transparent text-blue-700 hover:bg-blue-200'}`}
                                >
                                    {val.charAt(0).toUpperCase() + val.slice(1)}
                                </button>
                            ))}
                        </div>
                        <HighchartsReact highcharts={Highcharts} options={graphOptions} />
                    </div>
                    <div className="px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">ROI History</h1>
                        </div>
                        <div className="flex gap-2 items-center mt-2 md:mt-0">
                            <input
                                type="text"
                                placeholder="Search by email, Plant Name, or date..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="border px-3 py-2 rounded"
                            />
                            {/* Date filters */}
                            <input
                                type="date"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                className="border px-3 py-2 rounded"
                                title="Start Date"
                            />
                            <span className="text-gray-500">to</span>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                className="border px-3 py-2 rounded"
                                title="End Date"
                            />
                            <button
                                onClick={exportToCSV}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Export
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Plant Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Generated Unit
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {paginatedTransactions.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex flex-col items-center">
                                                <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                                </svg>
                                                <p className="text-lg font-medium">No ROI history found</p>
                                                <p className="text-sm">Your ROI history will appear here</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedTransactions.map((transaction, index) => (
                                        <tr key={transaction._id || index} className="hover:bg-gray-50 transition-colors duration-200">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {transaction.email}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {transaction.plantname}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-semibold text-gray-900">
                                                    {transaction.perunit} 
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {isValidDate(parseDate(transaction.date)) ? formatDate(transaction.date) : "-"}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 py-4">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-1 rounded border ${page === currentPage
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                        }`}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 rounded border bg-gray-100 text-gray-700 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                    {filteredTransactions.length > 0 && (
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{filteredTransactions.length}</span> ROI records
                                </div>
                                <div className="text-sm text-gray-500">
                                    Last updated: {new Date().toLocaleString()}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoiHistory; 